import { differenceInMinutes, minutesToHours } from 'date-fns';

import type { ISchedule, IScheduleBlockAvailable, ISortedAvailableShift, ScheduleId } from '@/types/schedule';

import { convertBlockToDate } from '@/helpers/date';

type ScheduleBlocksMapType = Record<string, ISortedAvailableShift[]>;
const groupSchedulesInPickupBasket = (
  shifts: ISortedAvailableShift[],
  timeZone: string,
): Record<string, ScheduleBlocksMapType> => {
  const groupedBySchedules = shifts.reduce<ScheduleBlocksMapType>((accumulator, currentShift) => {
    const keyLabel = `schedule_${currentShift.scheduleId}`;

    if (!accumulator[keyLabel]) {
      accumulator[keyLabel] = [];
    }

    accumulator[keyLabel] = [...accumulator[keyLabel], currentShift];
    return accumulator;
  }, {});

  return Object.entries(groupedBySchedules).reduce<Record<string, ScheduleBlocksMapType>>((acc, [key, values]) => {
    acc[key] = groupPickedUpShiftsByDay(values, timeZone);
    return acc;
  }, {});
};

const groupPickedUpShiftsByDay = (
  shifts: ISortedAvailableShift[],
  timeZone: string,
): Record<string, ISortedAvailableShift[]> => {
  return shifts.reduce<Record<string, ISortedAvailableShift[]>>((accumulator, currentShift) => {
    const keyLabel = `month_day_${convertBlockToDate(currentShift.startBlock, timeZone).getDate()}`;

    if (!accumulator[keyLabel]) {
      accumulator[keyLabel] = [];
    }

    accumulator[keyLabel] = [...accumulator[keyLabel], currentShift];
    accumulator[keyLabel] = accumulator[keyLabel].sort((a, b) => a.startBlock - b.startBlock);
    return accumulator;
  }, {});
};

const getScheduleShortName = (schedules: ISchedule[], scheduleId: ScheduleId): string => {
  const schedule = schedules.find(item => item.scheduleId === scheduleId);
  if (!schedule || !schedule.scheduleNameShort) return '';

  return schedule.scheduleNameShort;
};
const getTotalSelectedShiftsDuration = (
  selectedAvailableShifts: IScheduleBlockAvailable[] | null,
  timeZone: string,
): string => {
  if (!selectedAvailableShifts) return '';

  const totalDurationInMinutes = selectedAvailableShifts.reduce<number>((accumulator, currentValue) => {
    const diff = differenceInMinutes(
      convertBlockToDate(currentValue.endBlock + 1, timeZone), // add 1 to get the end of block
      convertBlockToDate(currentValue.startBlock, timeZone),
    );

    accumulator = accumulator + diff;
    return accumulator;
  }, 0);

  const minutesLeft = totalDurationInMinutes % 60;
  const hours = minutesToHours(totalDurationInMinutes - minutesLeft);

  return `${hours}h ${minutesLeft}min`;
};

const getUnpickedShifts = (
  unpickedShiftsIds: number[],
  selectedShiftsIds: ISortedAvailableShift[],
): ISortedAvailableShift[] => {
  selectedShiftsIds.reduce<ISortedAvailableShift[]>((accumulator, current) => {
    if (unpickedShiftsIds.indexOf(current.shiftId) === -1) {
      accumulator.push(current);
    }
    return accumulator;
  }, []);
  return selectedShiftsIds;
};

export {
  groupPickedUpShiftsByDay,
  groupSchedulesInPickupBasket,
  getScheduleShortName,
  getTotalSelectedShiftsDuration,
  getUnpickedShifts,
};
